import { isElevarTrackingAllowed } from './consent.ts';
import { type ElevarEvent } from './types.ts';

declare global {
    interface Window {
        ElevarDataLayer?: ElevarEvent[];
        ElevarInvalidateContext?: () => void;
    }
}

const hasWindow = (): boolean => typeof window !== 'undefined';

if (hasWindow()) {
    window.ElevarDataLayer = window.ElevarDataLayer ?? [];
}

// Elevar requires dl_user_data before action events after a context invalidation. User attributes
// resolve asynchronously, so action events fired in the meantime are held back rather than dropped.
let hasUserDataSinceContext = false;
const pendingElevarEvents: ElevarEvent[] = [];

// Consent can flip to allowed mid page view, after dl_user_data was already dropped and with
// nothing to re-push it. Hold it so the first allowed event can replay it.
let deferredUserDataEvent: ElevarEvent | null = null;

export const hasPushedElevarUserData = (): boolean => hasUserDataSinceContext;

const ensureElevarDataLayer = (): ElevarEvent[] => {
    if (!Array.isArray(window.ElevarDataLayer)) {
        window.ElevarDataLayer = [];
    }
    return window.ElevarDataLayer;
};

const pushUserData = (dataLayer: ElevarEvent[], event: ElevarEvent): void => {
    deferredUserDataEvent = null;
    dataLayer.push(event);
    hasUserDataSinceContext = true;
    pendingElevarEvents.splice(0).forEach(queuedEvent => {
        dataLayer.push(queuedEvent);
    });
};

// Returns whether the event was accepted, meaning pushed or held for the pending dl_user_data.
// Callers that fire an event only once must key that off this, not off attempting the push.
export function pushElevarEvent(event: ElevarEvent): boolean {
    if (!hasWindow()) {
        return false;
    }
    if (!isElevarTrackingAllowed()) {
        if (event.event === 'dl_user_data') {
            deferredUserDataEvent = event;
        }
        return false;
    }

    const dataLayer = ensureElevarDataLayer();

    if (event.event === 'dl_user_data') {
        pushUserData(dataLayer, event);
        return true;
    }

    const deferred = deferredUserDataEvent;
    if (!hasUserDataSinceContext && deferred) {
        pushUserData(dataLayer, deferred);
    }

    if (!hasUserDataSinceContext) {
        pendingElevarEvents.push(event);
        return true;
    }

    dataLayer.push(event);
    return true;
}

export function invalidateElevarContext(): void {
    hasUserDataSinceContext = false;
    pendingElevarEvents.length = 0;
    deferredUserDataEvent = null;
    if (!hasWindow()) {
        return;
    }
    // eslint-disable-next-line new-cap -- global name is defined by Elevar's script
    window.ElevarInvalidateContext?.();
}
